/**
 * Cycle de vie d'un item du brain — phase 5.
 *
 * Ce que remplace ce fichier : la mécanique d'enchaînement de `brain.js`
 * (`createTest` 590, `displayTest` 739, `hideTest` 806, `createNextTest`,
 * `clearEverything`, `computeResponse` 121, `allDropzonesAreFilled`,
 * `goodTutorialAnswer` 445).
 *
 * CE QU'IL NE FAIT PAS, ET POURQUOI
 *
 * Aucun accès aux stores Pinia, aux modales, au minuteur ni à l'API. `brain.js`
 * mélangeait les trois — la mécanique d'un item, l'état de l'écran et les
 * appels réseau — et c'est ce mélange qui le rend impossible à vérifier :
 * on ne peut pas dérouler un item sans démarrer toute l'application.
 *
 * Ici le runner ne connaît que la scène. Les CTA, les modales, le minuteur et
 * le `PATCH` restent à la charge de la vue, qui est déjà branchée sur les
 * stores. La frontière est le contrat exposé plus bas, dont la vue se sert
 * pour se substituer au legacy sans que rien d'autre ne change dans l'app.
 */
import gsap from "gsap"
import { computed, ref, shallowRef } from "vue"

import {
  useBrainAnimation,
  type AnswerStep,
  type BrainAnimation,
} from "@/features/brain/composables/useBrainAnimation"
import {
  useBrainInteraction,
  type BrainInteraction,
} from "@/features/brain/composables/useBrainInteraction"
import { calculateDropzoneValue } from "@/features/brain/domain/fusion"
import type { RawSceneData } from "@/features/brain/domain/geometry"

/** brain.js:733 — l'apparition ne part qu'une demi-seconde après la création. */
const DISPLAY_DELAY = 0.5
/** brain.js:781 — puis on attend encore une seconde avant d'autoriser le jeu. */
const READY_DELAY = 1

/** Un item tel que renvoyé par l'API. */
export type BrainItem = {
  id?: string
  timer?: number
  test_completed?: boolean
  data?: RawSceneData
}

/** Le comptage d'actions que l'app envoie avec la réponse. */
export type ResponseCounters = {
  timeInitial: number
  moves: number
  resets: number
  oversights: number
}

/** Ce que `computeResponse` produit — la charge utile du PATCH. */
export type BrainResponse = {
  item_id: string | undefined
  time_initial: number
  moves: number
  resets: number
  oversights: number
  incomplete: boolean
  answers: Record<string, string>
}

export function useBrainRunner() {
  /**
   * Les items, dans l'ordre de passage. Tableau NON réactif.
   *
   * Personne ne doit donc dériver un état de son contenu : un `computed` qui
   * lirait `data[i]` ne serait jamais invalidé quand on remplace l'item à
   * indice constant — et c'est exactement ce que fait `sendAnswer` à chaque
   * réponse (`data.length = 0 ; data.push(next)`, puis `build(0)`).
   */
  const data: BrainItem[] = []

  /** brain.js:44 — -1 tant qu'aucun item n'est affiché. */
  const currentTest = ref(-1)

  const interaction = shallowRef<BrainInteraction | null>(null)
  const animation = shallowRef<BrainAnimation | null>(null)

  /**
   * L'item construit par le dernier `build()`.
   *
   * Posé DANS `build()`, au même instant et depuis la même source que
   * `interaction`. L'ancienne version était un `computed` sur
   * `data[currentTest]` : il ne dépendait que de `currentTest`, qui reste à 0
   * d'un item à l'autre. L'interaction passait à l'item suivant, `item` restait
   * figé sur l'ancien — et `computeResponse` renvoyait l'ancien `item_id`. Le
   * serveur répondait donc toujours le même item suivant : boucle infinie.
   */
  const current = shallowRef<BrainItem | null>(null)

  /** L'item courant, ou `null` entre deux items. */
  const item = computed<BrainItem | null>(() =>
    currentTest.value > -1 ? current.value : null
  )

  /** Vrai pendant les animations d'entrée et de sortie : le jeu est bloqué. */
  const isTransitioning = ref(false)

  /**
   * Numéro de génération de la scène, incrémenté à chaque `build`.
   *
   * `show` et `hide` durent plusieurs secondes. Sans ce garde-fou, une
   * transition lancée sur l'item N peut se terminer APRÈS qu'on est passé à
   * l'item N+1, et appeler son `onReady` — donc démarrer le minuteur ou faire
   * clignoter les dropzones de la mauvaise scène. Le défaut existe déjà dans
   * brain.js, où rien n'empêche deux `displayTest` de se chevaucher ; on ne le
   * reproduit pas.
   */
  let generation = 0

  /**
   * Monte la scène d'un item, sans l'afficher.
   *
   * Séparé de `show()` parce que `brain.js` fait de même : il construit la
   * scène, applique les rotations, puis attend 500 ms avant l'animation
   * (brain.js:730-735). Les deux temps sont distincts et observables.
   */
  function build(index: number): boolean {
    const scene = data[index]?.data
    if (!scene?.masks?.length) return false

    current.value = data[index]
    currentTest.value = index
    generation++
    const nextInteraction = useBrainInteraction(scene)
    interaction.value = nextInteraction
    const nextAnimation = useBrainAnimation(
      computed(() => nextInteraction.layout)
    )
    // La scène naît INVISIBLE : `show()` attend une demi-seconde avant de
    // lancer l'apparition, et sans ça elle serait pleinement affichée pendant
    // ce temps-là, puis masquée d'un coup pour réapparaître en fondu.
    nextAnimation.prepare()
    animation.value = nextAnimation
    return true
  }

  /**
   * brain.js:733-777 — attend, joue l'apparition, puis laisse une seconde.
   *
   * `onReady` est appelé à la toute fin : c'est là que `brain.js` démarre le
   * minuteur hors tutoriel, et fait clignoter les dropzones vides en tutoriel.
   */
  async function show(onReady?: () => void): Promise<void> {
    const current = animation.value
    if (!current) return
    const mine = generation
    isTransitioning.value = true

    await sleep(DISPLAY_DELAY)
    if (generation !== mine) return
    await current.enter()
    if (generation !== mine) return
    await sleep(READY_DELAY)
    if (generation !== mine) return

    isTransitioning.value = false
    onReady?.()
  }

  /** brain.js:806-859 — joue la disparition. */
  async function hide(): Promise<void> {
    const current = animation.value
    if (!current) return
    const mine = generation
    isTransitioning.value = true
    await current.leave()
    if (generation !== mine) return
    isTransitioning.value = false
  }

  /**
   * brain.js:219-256 — remet l'item dans son état initial, sans animation.
   *
   * `rawResetTest` incrémente le compteur de resets et masque les CTA ; ces
   * deux effets appartiennent à la vue, qui les enchaîne après cet appel.
   */
  function reset() {
    animation.value?.stopAnswer()
    animation.value?.reset()
    interaction.value?.reset()
  }

  /** brain.js:1900 — toutes les dropzones portent au moins un token. */
  const allDropzonesFilled = computed(
    () => interaction.value?.allDropzonesFilled.value ?? false
  )

  /** brain.js:1911 — au moins une dropzone est remplie. */
  const anyDropzoneFilled = computed(
    () => interaction.value?.anyDropzoneFilled.value ?? false
  )

  /** brain.js:287-306 — fait clignoter les dropzones encore vides. */
  function blinkEmptyDropzones() {
    interaction.value?.blinkEmptyDropzones()
  }

  /**
   * brain.js:121-140 — la charge utile envoyée à l'API.
   *
   * `"NA"` sur une dropzone sans fusion est la valeur nulle attendue par R —
   * commentaire d'origine, conservé tel quel.
   */
  function computeResponse(counters: ResponseCounters): BrainResponse {
    const answers: Record<string, string> = {}
    for (const dz of interaction.value?.dropzones ?? []) {
      const tokens = dz.tokenKeys
        .map((key) => interaction.value?.tokens.find((t) => t.key === key))
        .filter((t): t is NonNullable<typeof t> => Boolean(t))
        .map((t) => ({ name: t.name, type: t.type, isNeg: t.isNeg }))
      answers[dz.name] = calculateDropzoneValue(tokens).texture || "NA"
    }

    return {
      item_id: item.value?.id,
      time_initial: counters.timeInitial,
      moves: counters.moves,
      resets: counters.resets,
      oversights: counters.oversights,
      incomplete: !allDropzonesFilled.value,
      answers,
    }
  }

  /**
   * brain.js:445-481 — la réponse du tutoriel est-elle la bonne ?
   *
   * Deux contrôles, comme l'original : chaque token attendu est bien dans SA
   * dropzone, et chaque dropzone porte exactement le nombre de tokens prévu.
   * Le second rattrape le cas où l'utilisateur a ajouté un token en trop.
   */
  function isTutorialAnswerCorrect(expected: AnswerStep[]): boolean {
    const live = interaction.value
    if (!live) return false

    const expectedPerDropzone = new Map<string, number>()
    let correct = true

    for (const step of expected) {
      expectedPerDropzone.set(
        step.dropzone,
        (expectedPerDropzone.get(step.dropzone) ?? 0) + 1
      )

      const token = live.tokens.find((t) => t.name === step.token)
      const dz = token?.dropzoneKey
        ? live.dropzones.find((d) => d.key === token.dropzoneKey)
        : undefined
      if (!token || !dz || dz.name !== step.dropzone) correct = false
    }

    for (const [name, count] of expectedPerDropzone) {
      const dz = live.dropzones.find((d) => d.name === name)
      if ((dz?.tokenKeys.length ?? 0) !== count) correct = false
    }

    return correct
  }

  /**
   * Le pilote dont `playAnswer` a besoin, câblé sur l'interaction courante.
   * Exposé ici pour que la vue n'ait pas à le reconstruire.
   */
  const answerDriver = {
    dropzoneCenter: (name: string) => {
      const dz = interaction.value?.dropzones.find((d) => d.name === name)
      return dz && interaction.value
        ? interaction.value.dropzoneCenter(dz)
        : null
    },
    tokenState: (name: string) =>
      interaction.value?.tokens.find((t) => t.name === name) ?? null,
    dropToken: (token: string, dropzone: string) => {
      interaction.value?.placeToken(token, dropzone)
    },
    stopBlink: (name: string) => {
      const dz = interaction.value?.dropzones.find((d) => d.name === name)
      if (dz) interaction.value?.stopBlink(dz)
    },
    reset,
  }

  /** brain.js:486-587 — joue la bonne réponse du tutoriel. */
  function playAnswer(steps: AnswerStep[]): Promise<void> {
    if (!animation.value) return Promise.resolve()
    return animation.value.playAnswer(steps, answerDriver)
  }

  return {
    data,
    currentTest,
    item,
    interaction,
    animation,
    isTransitioning,
    allDropzonesFilled,
    anyDropzoneFilled,
    build,
    show,
    hide,
    reset,
    blinkEmptyDropzones,
    computeResponse,
    isTutorialAnswerCorrect,
    playAnswer,
    answerDriver,
  }
}

export type BrainRunner = ReturnType<typeof useBrainRunner>

/**
 * Attente en secondes, sur l'HORLOGE GSAP et non `setTimeout`.
 *
 * brain.js utilisait `setTimeout` pour ces deux temps morts (500 ms avant
 * l'apparition, 1 s après). On s'en écarte volontairement, pour une raison
 * mesurable : dans un onglet masqué le navigateur bride
 * `requestAnimationFrame` — donc les tweens — mais continue de déclencher les
 * `setTimeout`. Deux horloges pour une même séquence, et l'enchaînement se
 * désynchronise dès que l'utilisateur change d'onglet.
 *
 * Une seule horloge, et la séquence reste cohérente quoi qu'il arrive. Effet
 * de bord utile : elle devient vérifiable, puisqu'on peut la faire avancer à
 * la main.
 */
function sleep(seconds: number): Promise<void> {
  return new Promise((resolve) => {
    gsap.delayedCall(seconds, resolve)
  })
}
